import React from 'react';
import * as Card from '../../v2BaseComponents/Card';
import { getIntl } from '../../../utils/intl';
import { APITypes } from '../../../primary-record-types';
import { codeStatusTypeMessages, poaRelationshipTypeMessages } from '../../../utils/sharedMessages';
import _truncate from 'lodash/truncate';
import { useMedicalProfileDirectives } from '../../../hooks/api/useMedicalProfileDirectives';

const intl = getIntl('profileDirectiveCard', {
    codeStatus: 'Code Status: {codeStatusType}',
    files: '{count} File{plural}',
    legalDecisionMaker: 'Legal Decision Maker',
});

export type DirectiveType = 'code-status' | 'poa';

const CodeStatusDirectiveCard: React.FC<
    Pick<ProfileDirectiveCardProps, 'onPress'> & {
        codeStatus: APITypes.medicalProfileService.ProfileCodeStatusResponse;
    }
> = (props) => {
    return (
        <Card.Card onPress={props.onPress} testID={`code-status-directive-card`}>
            <Card.CardContent>
                <Card.CardHeader>
                    {intl('codeStatus', {
                        codeStatusType: codeStatusTypeMessages(props.codeStatus.codeStatus),
                    })}
                </Card.CardHeader>
                <Card.CardBody2>
                    {props.codeStatus.codeStatusNotes
                        ? _truncate(props.codeStatus.codeStatusNotes, { length: 100 })
                        : ''}
                </Card.CardBody2>
                {props.codeStatus.codeStatusFiles.length ? (
                    <Card.CardChip
                        title={intl('files', {
                            count: props.codeStatus.codeStatusFiles.length,
                            plural: props.codeStatus.codeStatusFiles.length !== 1 ? 's' : '',
                        })}
                    />
                ) : null}
            </Card.CardContent>
        </Card.Card>
    );
};

const POADirectiveCard: React.FC<
    Pick<ProfileDirectiveCardProps, 'onPress'> & {
        poa: APITypes.medicalProfileService.ProfilePOAResponse;
    }
> = (props) => {
    return (
        <Card.Card onPress={props.onPress} testID={`poa-directive-card`}>
            <Card.CardContent>
                <Card.CardHeader>{intl('legalDecisionMaker')}</Card.CardHeader>
                <Card.CardBody2>
                    {`${props.poa.poaHealthcareProxyName}${
                        props.poa.poaRelationship ? ` (${poaRelationshipTypeMessages(props.poa.poaRelationship)})` : ''
                    }`}
                </Card.CardBody2>
                {props.poa.poaPhoneNumber ? <Card.CardBody2>{props.poa.poaPhoneNumber}</Card.CardBody2> : null}
                {props.poa.poaFiles.length ? (
                    <Card.CardChip
                        title={intl('files', {
                            count: props.poa.poaFiles.length,
                            plural: props.poa.poaFiles.length !== 1 ? 's' : '',
                        })}
                    />
                ) : null}
            </Card.CardContent>
        </Card.Card>
    );
};

export type ProfileDirectiveCardOwnProps = {
    medicalProfileId: string;
    directiveType: DirectiveType;
    onPress?: () => void;
};

export type ProfileDirectiveCardProps = ProfileDirectiveCardOwnProps;

const ProfileDirectiveCard: React.FC<ProfileDirectiveCardProps> = (props) => {
    const { isLoadingCodeStatus, codeStatus, isLoadingPOA, poa } = useMedicalProfileDirectives({
        medicalProfileId: props.medicalProfileId,
    });

    if (props.directiveType === 'code-status') {
        if (isLoadingCodeStatus || !codeStatus) return null;

        return <CodeStatusDirectiveCard codeStatus={codeStatus} onPress={props.onPress} />;
    } else {
        if (isLoadingPOA || !poa) return null;

        return <POADirectiveCard poa={poa} onPress={props.onPress} />;
    }
};

export default React.memo(ProfileDirectiveCard);

export const UnloadedProfileDirectiveCard: React.FC<ProfileDirectiveCardProps> = (props) => {
    const { isLoadingCodeStatus, isLoadingPOA } = useMedicalProfileDirectives({
        medicalProfileId: props.medicalProfileId,
    });

    const isLoading = props.directiveType === 'code-status' ? isLoadingCodeStatus : isLoadingPOA;

    return isLoading ? <Card.CardSkeleton /> : <ProfileDirectiveCard {...props} />;
};
